import React, {MutableRefObject, ReactNode, useEffect, useRef, useState} from 'react';
import {Popper} from '~/components/Popper';
import {classByIntent, Intent} from '~/lib/intent';
import {useKeyPressCallback} from '~/lib/keyPress';
import {useOutsideClick} from '~/lib/outsideClick';
import style from './index.module.scss';

interface IMenuProps {
    parentRef?: MutableRefObject<HTMLElement>;
    items: IMenuItemProps[];
    hover?: boolean;
    closeOnClick?: boolean;
}

export interface IMenuItemProps {
    children: string;
    icon?: ReactNode;
    intent?: Intent;
    onClick?: () => void;
    className?: string;
    items?: IMenuItemProps[];
}

export const MenuItem = ({
    icon,
    intent,
    children,
    onClick,
    items,
    className
}: IMenuItemProps) => {

    const classes = [classByIntent(intent), style.menuItem];
    const itemRef = useRef();
    return <div className={classes.join(' ')} onClick={!items && onClick} ref={itemRef}>
        {icon && <span className={style.menuItemIcon}>{icon}</span>}
        <span className={[style.menuItemChildren, className].join(' ')}>{children}</span>
        {items && <>
            <span className={style.subMenuIcon}><i className='fa fa-caret-right'/></span>
            {/* eslint-disable-next-line @typescript-eslint/no-use-before-define */}
            <Menu parentRef={itemRef} items={items} hover/>
        </>}
    </div>;
};

export const Menu = ({items, parentRef, closeOnClick = true, hover}: IMenuProps) => {
    const [open, setOpen] = useState<boolean>(false);

    const menuRef = useRef();

    const onOpenEvent = () => {
        setOpen(true);
    };

    useEffect(() => {
        parentRef.current.addEventListener('click', onOpenEvent);
        return () => {
            if (parentRef.current) {
                parentRef.current.removeEventListener('click', onOpenEvent);
            }
        };
    }, []);

    useOutsideClick(menuRef, () => setOpen(false));
    useKeyPressCallback('Escape', () => setOpen(false));

    return open && <Popper parentRef={parentRef} strategy='absolute'>
        <div ref={menuRef} className={style.menu}>{items.map((item, index) =>
            <MenuItem {...item} key={index} onClick={() => {
                if (closeOnClick) {
                    setOpen(false);
                }
                item.onClick();
            }}/>)}</div>
    </Popper>;
};
